import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import P11AdminLayout from "./P11AdminLayout";
import { useP11Role } from "./P11AdminGuard";
import { P11 } from "./p11shared";
import { TechnicalGlyph } from "./p11fx";

// PENDING APPROVAL — nieuwe aanmeldingen wachten op beoordeling (owner-only acties).
function PendingApproval({ onChanged }) {
  const role = useP11Role();
  const [items, setItems] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/project11/admin/participants?status=PENDING_APPROVAL").then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const act = async (p, action, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/project11/admin/participants/${p.id}/${action}`, extra);
      if (action === "approve") toast.success(`Goedgekeurd — ${data.number_display}`);
      else if (action === "waitlist") toast.success("Op wachtlijst gezet");
      else toast.success("Afgewezen");
      setRejectTarget(null); setReason("");
      load(); onChanged?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d === "PROJECT_FULL") toast.error("Project is vol — verhoog capaciteit of zet op wachtlijst");
      else toast.error(typeof d === "string" ? d : "Actie mislukt");
    } finally { setBusy(false); }
  };

  if (items === null || items.length === 0) return null;
  return (
    <section className="border p-6 mb-8" style={{ borderColor: P11.yellow }} data-testid="p11-pending-section">
      <div className="p11-label-yellow mb-5 inline-flex items-center gap-2"><TechnicalGlyph type="crosshair" size={13} active />PENDING APPROVAL — {items.length}</div>
      <div className="space-y-4">
        {items.map((p) => (
          <div key={p.id} className="border p-4 flex flex-wrap items-center gap-4" style={{ borderColor: P11.border }} data-testid={`p11-pending-row-${p.id}`}>
            <div className="flex-1 min-w-48">
              <div className="text-sm" style={{ color: P11.text }}>{p.name}</div>
              <div className="p11-label mt-1">{p.email} · {p.created_at?.slice(0, 10)} · {p.type}</div>
            </div>
            {role === "owner" && (
              <div className="flex gap-2">
                <button onClick={() => act(p, "approve")} disabled={busy} className="p11-label px-3 py-1.5 border" style={{ borderColor: P11.yellow, color: P11.yellow }} data-testid={`p11-pending-approve-${p.id}`}>APPROVE</button>
                <button onClick={() => act(p, "waitlist")} disabled={busy} className="p11-label px-3 py-1.5 border" style={{ borderColor: P11.border, color: P11.gray2 }} data-testid={`p11-pending-waitlist-${p.id}`}>MOVE TO WAITLIST</button>
                <button onClick={() => setRejectTarget(p)} disabled={busy} className="p11-label px-3 py-1.5 border" style={{ borderColor: "#7a2d2d", color: "#e08585" }} data-testid={`p11-pending-reject-${p.id}`}>REJECT</button>
              </div>
            )}
          </div>
        ))}
      </div>
      {rejectTarget && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setRejectTarget(null)}>
          <div className="bg-[#080808] border max-w-sm w-full p-8" style={{ borderColor: P11.border }} onClick={(e) => e.stopPropagation()} data-testid="p11-reject-modal">
            <div className="p11-label-yellow mb-4">// AFWIJZEN — {rejectTarget.name}</div>
            <p className="text-sm mb-4" style={{ color: P11.gray2 }}>De reden is alleen intern zichtbaar en wordt niet aan de deelnemer gedeeld.</p>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Interne reden (optioneel)…" className="p11-input" data-testid="p11-reject-reason" />
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setRejectTarget(null)} className="p11-btn-ghost" data-testid="p11-reject-no">ANNULEREN</button>
              <button onClick={() => act(rejectTarget, "reject", { reason })} disabled={busy} className="p11-btn" data-testid="p11-reject-yes">{busy ? "…" : "AFWIJZEN"}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// Deelnemersdirectory + handmatig toevoegen (FULL / LATE ENTRY) met capaciteitsconflict-flow.
export default function P11AdminParticipants() {
  const nav = useNavigate();
  const role = useP11Role();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [form, setForm] = useState(null); // null | {}
  const [capacityConflict, setCapacityConflict] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ first_name: "", last_name: "", dob: "", email: "", phone: "", type: "FULL" });

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    api.get(`/project11/admin/participants?${params}`).then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  };
  useEffect(load, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (increase = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/project11/admin/participants", { ...f, increase_capacity: increase });
      toast.success(`Deelnemer toegevoegd — ${data.number_display}`);
      setForm(null); setCapacityConflict(false);
      setF({ first_name: "", last_name: "", dob: "", email: "", phone: "", type: "FULL" });
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d === "PROJECT_FULL") setCapacityConflict(true);
      else if (d === "UNDERAGE") toast.error("Deelnemer is jonger dan 18 op de eerste trainingsdatum");
      else if (d === "ALREADY_REGISTERED") toast.error("E-mailadres is al ingeschreven");
      else if (d === "FULL_PROGRAM_NOT_POSSIBLE") toast.error("Volledig programma niet meer mogelijk — kies LATE ENTRY");
      else toast.error(typeof d === "string" ? d : "Toevoegen mislukt");
    } finally { setBusy(false); }
  };

  return (
    <P11AdminLayout>
      <div data-testid="p11-admin-participants">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="p11-label-yellow">PARTICIPANTS</div>
            <h1 className="p11-h1 text-3xl mt-4">DIRECTORY.</h1>
          </div>
          {role === "owner" && (
            <button onClick={() => setForm({})} className="p11-btn" data-testid="p11-add-participant">ADD PARTICIPANT +</button>
          )}
        </div>
        <div className="flex flex-wrap gap-3 mb-6">
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Zoek op naam, e-mail of nummer…" className="p11-input flex-1 min-w-56" data-testid="p11-participants-search" />
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="p11-input !w-auto bg-[#080808]" data-testid="p11-participants-status">
            <option value="">ALLE</option>
            {["CONFIRMED", "PENDING_APPROVAL", "WAITLISTED", "NOT_ENROLLED", "REJECTED", "CANCELLED"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={load} className="p11-btn-ghost" data-testid="p11-participants-apply">ZOEK</button>
        </div>

        <PendingApproval onChanged={load} />

        {items === null ? <span className="p11-label">…</span> : items.length === 0 ? (
          <div className="border p-10 text-center" style={{ borderColor: P11.border }} data-testid="p11-participants-empty">
            <div className="p11-h2 text-xl">Nog geen deelnemers.</div>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: P11.border }} data-testid="p11-participants-list">
            {items.map((p) => (
              <button key={p.id} onClick={() => nav(`/project11/admin/participants/${p.id}`)} className="w-full text-left py-4 flex flex-wrap items-center gap-3 hover:bg-[#171717] transition-colors px-2" data-testid={`p11-participant-row-${p.id}`}>
                <span className="p11-mono w-16" style={{ color: p.number_display ? P11.yellow : P11.gray }}>{p.number_display || "———"}</span>
                <span className="flex-1 min-w-40 text-sm" style={{ color: P11.text }}>{p.name}</span>
                <span className="p11-label hidden md:inline">{p.email}</span>
                <span className="p11-label" style={{ color: P11.gray2 }}>{p.type}</span>
                <span className="p11-label px-2 py-1 border" style={{ borderColor: p.status === "CONFIRMED" ? P11.yellow : P11.border, color: p.status === "CONFIRMED" ? P11.yellow : P11.gray2 }}>{p.status}</span>
                <span className="p11-mono text-sm" style={{ color: P11.gray2 }}>{String(p.verified).padStart(2, "0")}/08</span>
                {p.equipment_help_requested && <span className="p11-label" style={{ color: P11.yellow }}>MATERIAAL?</span>}
              </button>
            ))}
          </div>
        )}

        {form !== null && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => { setForm(null); setCapacityConflict(false); }}>
            <div className="bg-[#080808] border max-w-lg w-full p-8 max-h-[85vh] overflow-y-auto" style={{ borderColor: P11.border }} onClick={(e) => e.stopPropagation()} data-testid="p11-add-form">
              <div className="p11-label-yellow mb-6">// ADD PARTICIPANT</div>
              <div className="space-y-6">
                <div><label className="p11-label">Voornaam *</label><input value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} className="p11-input mt-1" data-testid="p11-add-first-name" /></div>
                <div><label className="p11-label">Achternaam *</label><input value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} className="p11-input mt-1" data-testid="p11-add-last-name" /></div>
                <div><label className="p11-label">Geboortedatum *</label><input type="date" value={f.dob} onChange={(e) => setF({ ...f, dob: e.target.value })} className="p11-input mt-1" data-testid="p11-add-dob" /></div>
                <div><label className="p11-label">E-mail *</label><input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="p11-input mt-1" data-testid="p11-add-email" /></div>
                <div><label className="p11-label">Telefoon</label><input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="p11-input mt-1" data-testid="p11-add-phone" /></div>
                <div>
                  <label className="p11-label">Inschrijftype</label>
                  <div className="flex gap-3 mt-2">
                    {["FULL", "LATE"].map((tp) => (
                      <button key={tp} onClick={() => setF({ ...f, type: tp })} className="p11-btn-ghost" style={f.type === tp ? { borderColor: P11.yellow, color: P11.yellow } : {}} data-testid={`p11-add-type-${tp.toLowerCase()}`}>{tp === "FULL" ? "FULL PROGRAM" : "LATE ENTRY"}</button>
                    ))}
                  </div>
                  <p className="p11-label mt-3" style={{ color: P11.gray }}>Ontbrekende gegevens en toestemmingen worden via een beveiligde uitnodiging door de deelnemer zelf afgerond.</p>
                </div>
                {capacityConflict && (
                  <div className="border p-5" style={{ borderColor: P11.yellow }} data-testid="p11-add-capacity-conflict">
                    <div className="p11-label-yellow">PROJECT CAPACITEIT BEREIKT</div>
                    <p className="text-sm mt-2" style={{ color: P11.gray2 }}>Verhoog de projectcapaciteit met 1 en voeg de deelnemer toe. Sessiecapaciteit blijft ongewijzigd.</p>
                    <button onClick={() => submit(true)} disabled={busy} className="p11-btn mt-4" data-testid="p11-add-increase-confirm">INCREASE CAPACITY AND ADD PARTICIPANT</button>
                  </div>
                )}
                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={() => { setForm(null); setCapacityConflict(false); }} className="p11-btn-ghost" data-testid="p11-add-cancel">ANNULEREN</button>
                  {!capacityConflict && <button onClick={() => submit(false)} disabled={busy} className="p11-btn" data-testid="p11-add-save">{busy ? "…" : "TOEVOEGEN"}</button>}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </P11AdminLayout>
  );
}
